/**
 * foliage_trample — the shared influence field that makes grass bend away from
 * whoever walks through it.
 *
 * The engine ships `InfluenceMap` (a world-XZ RG16F field: agents deposit a
 * bend vector, the field decays back to upright) and a material modifier for
 * it. The modifier was never wired into Atlas — and it could not be, as-is,
 * because it REPLACES `material.positionNode` and the grass materials already
 * own theirs for the wind sway. So this owns the CPU half and `lib/wind.ts`
 * samples the field inside the ONE position node the blades already have.
 *
 * CAMERA-FOLLOWING. The field is a fixed-size rectangle around the player
 * rather than the whole 2,048 km continent. It re-centres when the player
 * leaves the middle of it, and re-centring CLEARS it — which is invisible in
 * practice because the decay is fast (a trail is gone in a couple of seconds),
 * and blades that far out are past the groundcover ring anyway.
 *
 * The bounds ride in ONE shared vec4 uniform (minX, minZ, rangeX, rangeZ), so
 * a re-centre is a single uniform write no matter how many species materials
 * are live.
 */
import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';
import { InfluenceMap } from 'enginex/foliage/InfluenceMap.js';
import { createInfluenceMapConfig, FOLIAGE_PRESETS } from 'enginex/foliage/InfluenceMapConfig.js';

/** Side length (m) of the field. Comfortably past the groundcover ring. */
export const TRAMPLE_REGION_M = 96;
/** Re-centre once the player is this far from the middle. */
const RECENTER_AT_M = TRAMPLE_REGION_M * 0.25;

/** Shared bounds for every grass material: (minX, minZ, rangeX, rangeZ). */
export const trampleBounds = new THREE.Vector4(-TRAMPLE_REGION_M / 2, -TRAMPLE_REGION_M / 2, TRAMPLE_REGION_M, TRAMPLE_REGION_M);
export const trampleBoundsNode = uniform(trampleBounds);

let map: InfluenceMap | null = null;
let centerX = 0;
let centerZ = 0;

/** The field's texture, or null before `initTrample`. */
export function trampleTexture(): THREE.Texture | null {
  return (map?.texture as THREE.Texture | undefined) ?? null;
}

export function trampleActive(): boolean { return map !== null; }

/**
 * Create the field at a quality preset. Idempotent; call `disposeTrample`
 * before changing quality.
 */
export function initTrample(quality: 'low' | 'medium' | 'high' | 'ultra'): InfluenceMap {
  if (map) return map;
  map = new InfluenceMap(createInfluenceMapConfig({
    quality: FOLIAGE_PRESETS[quality],
    bounds: { minX: -TRAMPLE_REGION_M / 2, minZ: -TRAMPLE_REGION_M / 2, maxX: TRAMPLE_REGION_M / 2, maxZ: TRAMPLE_REGION_M / 2 },
    // Grass springs back in about a second — fast enough that a re-centre is
    // invisible, slow enough that you see your own trail behind you.
    decay: 1.1,
    maxBend: 0.45,
    autoUpload: false, // uploaded once per frame by stepTrample
  }));
  return map;
}

export function disposeTrample(): void {
  map?.dispose();
  map = null;
}

/**
 * Advance the field one frame: re-centre on the player if needed, decay, take
 * this frame's agents, and upload.
 *
 * `agents` are world-space; anything outside the region is skipped by the map
 * itself. Pass the player first — the agent cap is a budget, and the player's
 * own footprint is the one that must never be dropped.
 */
export function stepTrample(
  dt: number,
  playerX: number,
  playerZ: number,
  agents: Array<{ x: number; z: number; radius: number; strength: number; dirX?: number; dirZ?: number }>,
): void {
  if (!map) return;
  if (Math.abs(playerX - centerX) > RECENTER_AT_M || Math.abs(playerZ - centerZ) > RECENTER_AT_M) {
    centerX = playerX; centerZ = playerZ;
    const half = TRAMPLE_REGION_M / 2;
    const b = map.config.bounds;
    b.minX = centerX - half; b.maxX = centerX + half;
    b.minZ = centerZ - half; b.maxZ = centerZ + half;
    trampleBounds.set(b.minX, b.minZ, TRAMPLE_REGION_M, TRAMPLE_REGION_M);
    map.clear(); // see the header: the decay makes this invisible
  }
  map.beginFrame();
  map.decay(dt);
  for (const a of agents) {
    map.depositAgent({ x: a.x, z: a.z, radius: a.radius, strength: a.strength, ...(a.dirX !== undefined && a.dirZ !== undefined ? { dirX: a.dirX, dirZ: a.dirZ } : {}) });
  }
  map.upload();
}

/** Diagnostics for the debug menu / probes. */
export function trampleStats(): { active: boolean; center: [number, number]; region: number; stats: unknown } {
  return { active: map !== null, center: [centerX, centerZ], region: TRAMPLE_REGION_M, stats: map?.stats ?? null };
}
